import React, { useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import FaqService from '~/api/services/faq';
import notify from '~/components/Notify';
import { El, StyledAccordion } from './style';
import { useNavigate } from 'react-router-dom';
import { Accordion, Icon, Input, Label, Message, Transition } from 'semantic-ui-react';
import { RouteTypes } from '~/routes/constants';
import Dialog from '~/components/Dialog';
import { GlobalEl } from '~/styles/app';
import { El as ElLayout } from '~/components/Layout/style'
import SortSidebar from './SortSidebar';
import { useDebounce } from 'use-lodash-debounce'
import { b64toBlob, handleErrors } from '~/utils/helpers';
import { useAuth } from '~/routes/components/AuthProvider';
import { ADMIN_ROLE_SLUG, OPERATIVE_ROLE_SLUG, imageTypes, pdfType } from '~/Constants';

const Faq: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [listGroups, setListGroups] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedValue = useDebounce(search, 300)
  const navigate = useNavigate();
  const [fileSource, setFileSource] = useState(null);
  const [activeAccordion, setActiveAccordion] = useState({
    groupIndexActive: -1,
    questionIndexActive: -1
  })
  const { systemData: { logged: { roleSlug }, texts } } = useAuth()
  const [sortGroupId, setSortGroupId] = useState(null)
  const [deleteFaqGroupId, setDeleteFaqGroupId] = useState(null);
  const [sortItems, setSortedItems] = useState(null)
  const editRole = (roleSlug === ADMIN_ROLE_SLUG) || (roleSlug === OPERATIVE_ROLE_SLUG)
  const faqText = texts.find(text => text.slug === 'faq_text')

  useEffect(() => {
    fetchFaqData();
  }, [debouncedValue, t]);

  async function fetchFaqData() {
    setIsBusy(true);
    try {
      const responseFaqGroups = await FaqService.getFaqIndex({ search: search });
      setListGroups(responseFaqGroups.data);
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  const renderSearch = () => {
    return (
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    );
  };

  const renderActions = () => {
    return (
      <>
        <ElLayout.PageHeaderActionButton
          onClick={() => setSortedItems(listGroups?.rows)}
        >
          {t('Sort groups')}
        </ElLayout.PageHeaderActionButton>
        <ElLayout.PageHeaderActionButton
          onClick={() => navigate(RouteTypes.FAQ_CREATE)}
        >
          {t('Add')}
        </ElLayout.PageHeaderActionButton>
      </>
    );
  };

  const handleDownloadFile = async (fileName, originalFilename) => {
    setIsBusy(true);
    try {
      const response = await FaqService.getFile({ fileName: fileName });
      const type = response.data.split(';')[0].split('data:')[1];
      const typeOriginalFilename = originalFilename.split('.')[1] || ''
      if (imageTypes.find(imageType => imageType === typeOriginalFilename) || typeOriginalFilename === pdfType) {
        const blob = b64toBlob(response.data, type);
        const blobUrl = URL.createObjectURL(blob);
        setFileSource(blobUrl);
      } else {
        // Create a download link
        const blob = b64toBlob(response.data, type);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = originalFilename; // Specify the desired file name
        document.body.appendChild(a);
        a.click();
        // Clean up resources
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  };

  const renderModalPreviewFile = () => {
    return (
      <Dialog
        open={Boolean(fileSource)}
        handleClose={() => setFileSource(null)}
        textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
        textHeader={t('Preview')}
        textClose={t('Close')}
        type={'preview'}
        isLoading={isBusy}
      />
    );
  };

  function renderAttachmentLabel(fileName, originalName) {
    return (
      <El.FilesContainer>
        <Label
          basic
          onClick={() => handleDownloadFile(fileName, originalName)}
          color='blue'
          as='a'
          content={originalName}
          icon='file'
        />
      </El.FilesContainer>
    );
  }

  const deleteFaqGroup = async (id: string) => {
    setIsBusy(true);
    try {
      await FaqService.deleteFaqGroup(id);
      notify(t('Faq group deleted'), 'success');
      setDeleteFaqGroupId(null);
      fetchFaqData();
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false);
  };

  function handleClickAccordion(e, titleProps, indexGroup) {
    const { index } = titleProps
    const differentGroup = indexGroup !== activeAccordion.groupIndexActive
    if (differentGroup) {
      setActiveAccordion({
        groupIndexActive: indexGroup,
        questionIndexActive: -1
      })
      setTimeout(() => {
        setActiveAccordion({
          groupIndexActive: indexGroup,
          questionIndexActive: activeAccordion.questionIndexActive === index ? differentGroup ? index : -1 : index
        })
      }, 250)
    } else {
      setActiveAccordion({
        groupIndexActive: indexGroup,
        questionIndexActive: activeAccordion.questionIndexActive === index ? -1 : index
      })
    }
  }

  function renderQuestionContent(item) {
    return <div>
      <div style={{ marginTop: '-10px', marginBottom: 15 }}>{item?.answer}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 15 }}>
        {item?.files?.map((file) =>
          renderAttachmentLabel(file?.filename, file?.originalFilename)
        )}
      </div>
    </div>
  }

  async function handleSaveSortedItems(items) {
    setIsBusy(true);
    try {
      if (!sortGroupId) {
        await FaqService.sortFaqGroups(items);
      } else {
        await FaqService.sortQuestionsInGroup(sortGroupId, items)
      }
      setSortedItems(null)
      setSortGroupId(null)
      notify(t('Faq group sorted'), 'success');
      fetchFaqData();
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }

    setIsBusy(false);
  }

  function renderNoResultFiles() {
    return <Message info style={{ margin: '20px auto', textAlign: 'center', width: '50%' }}>
      <Message.Header>{t('No results')}</Message.Header>
      <p>{t('Sorry, there are no groups to display.')}</p>
    </Message>
  }

  function renderGroups() {
    return listGroups?.rows.map((group, groupIndex) => {
      const { groupIndexActive, questionIndexActive } = activeAccordion
      return (
        <div key={Math.random()}>
          <El.GroupsContainer>
            <El.GroupNameTitle>{group?.name}</El.GroupNameTitle>
            {editRole && <div>
              <GlobalEl.DefaultLinkButton
                onClick={() => {
                  setSortGroupId(group.id)
                  setSortedItems(group.questions)
                }}
              >
                <Icon className='btn-icon' name='sort'></Icon>
                {t('Sort questions')}
              </GlobalEl.DefaultLinkButton>
              <GlobalEl.DefaultLinkButton
                onClick={() => navigate(RouteTypes.FAQ_EDIT.replace(':id', group?.id))}
              >
                <Icon className='btn-icon' name='pencil alternate'></Icon>
                {t('Edit')}
              </GlobalEl.DefaultLinkButton>
              <GlobalEl.DefaultLinkButton
                className='danger'
                onClick={() => {
                  setDeleteFaqGroupId(group.id);
                }}
              >
                <Icon className='btn-icon' name='trash alternate outline'></Icon>
                {t('Delete')}
              </GlobalEl.DefaultLinkButton>
            </div>
            }
          </El.GroupsContainer>
          {group?.questions.map((item, index) => {
            const active = questionIndexActive === index && groupIndexActive === groupIndex
            return (
              <StyledAccordion
                styled
                key={group.id + item.id}
              >
                <Accordion.Title
                  onClick={(e, data) => handleClickAccordion(e, data, groupIndex)}
                  index={index}
                  active={active}
                >
                  <div> {item.question}</div>
                  <Icon name={active ? 'minus' : 'plus'} />
                </Accordion.Title>
                <Transition visible={active} animation='slide down' duration={200}>
                  <Accordion.Content
                    active={active}
                  >
                    {renderQuestionContent(item)}
                  </Accordion.Content>
                </Transition>
              </StyledAccordion>
            );
          })}
        </div>
      );
    })
  }

  return (
    <Layout
      segmented
      filters={renderSearch()}
      actions={editRole && renderActions()}
      isBusy={isBusy}
      title={faqText?.translation}
    >
      {listGroups?.rows?.length > 0 ? renderGroups() : renderNoResultFiles()}
      {renderModalPreviewFile()}
      <Dialog
        isDialogOpen={!!deleteFaqGroupId}
        textClose={t('No')}
        type={'abort'}
        textContent={t('Do you really want to delete this FAQ group?')}
        textHeader={t('Delete confirmation')}
        handleClose={() => setDeleteFaqGroupId(null)}
        handleSubmit={() => deleteFaqGroup(deleteFaqGroupId)}
        textSubmit={t('Yes')}
      />
      <SortSidebar
        items={sortItems || []}
        groupId={sortGroupId}
        isVisible={Boolean(sortItems)}
        onClose={() => {
          setSortGroupId(null)
          setSortedItems(null)
        }}
        handleSave={(items) => handleSaveSortedItems(items)}
      />
    </Layout>
  );
};

export default Faq;
